import { Store } from '../../../store/index';
import {
    resetCallkitData,
    setIncomingCallDetails,
    initializeParticipants,
    updateParticipantStatus,
    removeParticipantFromCall,
    setCallType,
    updateCallSwitch,
    setCallScreenStatus,
    setCallUIState,
    addParticipantToCall,
    setCallLink,
    setCallMode,
    setMissedCallCount,
    triggerCallLogRefresh,
    swapSpeakerIntoVisible,
    setWaitingCall,
    clearWaitingCall,
    setHeldCall,
    clearHeldCall,
} from '../model/callkitDataSlice';
import { isEnabled } from '../subFeatureRegistry';
import {
    CALL_SWITCH_ACCEPT,
    CALL_SWITCH_CANCEL,
    CALL_MODES,
    CALL_TYPE,
    CALLKIT_CALLSCREENSTATUS,
    CALLKIT_CALLSTATUS,
    CALLKIT_CALLUISTATE,
    CALL_LINK_UPDTAE,
    CALL_TYPE_UPDATE,
    CALL_MODE_UPDATE,
    CALL_KIT_SCREENSHARE_STATE,
} from '../constants';
import SDK from '../../../Components/SDK';
import { setUserInfoMap } from '../../../slice/userInfoSlice';
import { deleteItemFromLocalStorage } from '../../../Components/WebChat/WebChatEncryptDecrypt';
import { showConfrence } from '../../../Actions/CallAction';
import logger from '../../../Helpers/logger';
import { i18n } from './i18n';
import { getLocalUserName, isMatchingUserId, resolveCallUserName } from './helper';
import { getCallLogs, toggleCallKitVideo } from './sdkCallMethods';
import { CallMediaStore } from './CallMediaStore';
import { showErrorToast, showWarningToast } from './toastMessage';
import {
    showIncomingCallNotification,
    closeIncomingCallNotification,
    showMissedCallNotification,
    isIncomingNotificationActive,
} from './browserNotification';

/**
 * Clear legacy call system state (localStorage + Redux showConfrenceData)
 * so the recent chat no longer shows "Ongoing audio call..." after CallKit ends.
 */
const clearLegacyCallState = () => {
    deleteItemFromLocalStorage('call_connection_status');
    deleteItemFromLocalStorage('roomName');
    deleteItemFromLocalStorage('callType');
    deleteItemFromLocalStorage('callingComponent');
    Store.dispatch(
        showConfrence({
            showComponent: false,
            showStreamingComponent: false,
            showCalleComponent: false,
            callStatusText: null,
            stopSound: true,
        }),
    );
};

const { getUsersProfile } = SDK;

const getParticipantAudioMuted = (userId, currentUserId, tracks) => {
    if (isMatchingUserId(userId, currentUserId)) return false;
    const audioTrack = tracks?.find(t => t.kind === 'audio' && (t.source || t.track?.source) !== 'screen_share_audio');
    return audioTrack ? audioTrack.isMuted : true;
};

const getParticipantVideoMuted = (userId, currentUserId, tracks) => {
    if (isMatchingUserId(userId, currentUserId)) {
        return Store.getState().callkitData.callType?.toUpperCase() !== CALL_TYPE.VIDEO;
    }
    const videoTrack = tracks?.find(t => t.kind === 'video' && (t.source || t.track?.source) !== 'screen_share');
    return videoTrack ? videoTrack.isMuted : true;
};

export const callkitCallbacks = {
    onIncomingCall: callData => {
        logger.debug('onIncomingCall SDK data:', callData);
        const currentUserId = getLocalUserName();
        const { callScreenStatus } = Store.getState().callkitData;

        const callerId = callData.from || callData.callerId;
        if (isMatchingUserId(callerId, currentUserId) || callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE) {
            logger.debug('Ignoring onIncomingCall: own call or user already in another call session');
            return;
        }

        const users = Store.getState().userInfoData?.users || {};
        const remoteIdentities = (callData.participants || [])
            .map(p => p.identity)
            .filter(userId => !isMatchingUserId(userId, currentUserId));

        const essentialIds = Array.from(new Set([callerId, ...remoteIdentities.slice(0, 2)]));
        const missingProfiles = essentialIds.filter(userId => userId && !users[userId]);

        if (missingProfiles.length > 0) {
            getUsersProfile(missingProfiles)
                .then(res => {
                    if (res?.users) {
                        Store.dispatch(setUserInfoMap(res.users));
                        showIncomingCallNotification(callData, Store.getState().userInfoData.users);
                    }
                })
                .catch(err => logger.error('onIncomingCall: Error fetching participant profiles:', err));
        } else {
            showIncomingCallNotification(callData, users);
        }

        const participants = (callData.participants || []).map(p => {
            const currentUserId = getLocalUserName();
            const isLocal = isMatchingUserId(p.identity, currentUserId);
            const isVideoCall = callData.callType?.toUpperCase() === CALL_TYPE.VIDEO;

            const participantData = {
                userId: p.identity,
                callStatus: p.callStatus,
                stream: null,
            };

            if (isLocal) {
                participantData.isAudioMuted = false;
                participantData.isVideoMuted = !isVideoCall;
            } else {
                const audioTrack = p.tracks?.find(
                    t => t.kind === 'audio' && (t.source || t.track?.source) !== 'screen_share_audio',
                );
                const videoTrack = p.tracks?.find(
                    t => t.kind === 'video' && (t.source || t.track?.source) !== 'screen_share',
                );

                participantData.isAudioMuted = audioTrack ? audioTrack.isMuted : true;
                participantData.isVideoMuted = videoTrack ? videoTrack.isMuted : true;
            }

            return participantData;
        });
        Store.dispatch(initializeParticipants(participants));
        Store.dispatch(setIncomingCallDetails(callData));
    },

    onCallWaiting: callData => {
        const callerId = callData.from || callData.callerId;
        const users = Store.getState().userInfoData?.users || {};

        if (callerId && !users[callerId]) {
            getUsersProfile([callerId])
                .then(res => res?.users && Store.dispatch(setUserInfoMap(res.users)))
                .catch(err => logger.error('onCallWaiting: Error fetching caller profile:', err));
        }

        Store.dispatch(setWaitingCall({ ...callData, callerId }));
    },

    onCallWaitingEnded: data => {
        Store.dispatch(clearWaitingCall());

        // Only Accept carries participants; decline and timeout leave the call untouched.
        const participants = data?.participants;
        if (!participants?.length) return;

        const currentUserId = getLocalUserName();
        Store.dispatch(
            initializeParticipants(
                participants.map(participant => ({
                    userId: participant.identity,
                    callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                    stream: null,
                    isAudioMuted: false,
                    isVideoMuted: data?.callType?.toUpperCase() !== CALL_TYPE.VIDEO,
                    isLocalUser: isMatchingUserId(participant.identity, currentUserId),
                })),
            ),
        );
        if (data?.callMode) Store.dispatch(setCallMode(data.callMode));
        Store.dispatch(setCallUIState(CALLKIT_CALLUISTATE.MAXIMIZED));
        Store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
    },

    onCallHeld: data => {
        const { ongoingCallParticipants, callType, callMode } = Store.getState().callkitData;
        const remote = ongoingCallParticipants?.find(participant => !participant.isLocalUser);

        Store.dispatch(
            setHeldCall({
                roomId: data.roomId,
                userId: remote?.userId,
                callType,
                callMode,
                heldAt: Date.now(),
            }),
        );
    },

    onCallResumed: data => {
        Store.dispatch(clearHeldCall());
        Store.dispatch(clearWaitingCall());

        // The screen was showing the call that just ended, so rebuild it from the resumed
        // call's participants rather than leaving an empty call view behind.
        const currentUserId = getLocalUserName();
        const participants = (data?.participants || []).map(participant => ({
            userId: participant.identity,
            callStatus: CALLKIT_CALLSTATUS.CONNECTED,
            stream: null,
            isAudioMuted: false,
            isVideoMuted: data?.callType?.toUpperCase() !== CALL_TYPE.VIDEO,
            isLocalUser: isMatchingUserId(participant.identity, currentUserId),
        }));

        if (participants.length) {
            Store.dispatch(initializeParticipants(participants));
        }
        if (data?.callType) Store.dispatch(setCallType(data.callType));
        Store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
    },

    // The held call ended on its own; nothing to return to once the active call finishes.
    onCallHeldEnded: () => {
        Store.dispatch(clearHeldCall());
    },

    onActiveParticipants: participants => {
        logger.debug('onActiveParticipants triggered:', participants);
    },

    onParticipantConnected: newParticipant => {
        logger.debug('onParticipantConnected triggered:', newParticipant);
        const userId = newParticipant.identity;
        const currentUserId = getLocalUserName();

        const { ongoingCallParticipants } = Store.getState().callkitData;
        const participantExists = ongoingCallParticipants.some(p => p.userId === userId);

        if (participantExists) {
            Store.dispatch(
                updateParticipantStatus({
                    userId: userId,
                    callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                    isAudioMuted: getParticipantAudioMuted(userId, currentUserId, newParticipant.tracks),
                    isVideoMuted: getParticipantVideoMuted(userId, currentUserId, newParticipant.tracks),
                }),
            );
        } else {
            Store.dispatch(
                addParticipantToCall({
                    userId: userId,
                    callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                    isAudioMuted: getParticipantAudioMuted(userId, currentUserId, newParticipant.tracks),
                    isVideoMuted: getParticipantVideoMuted(userId, currentUserId, newParticipant.tracks),
                }),
            );
        }
    },

    onParticipantDisConnected: participant => {
        logger.debug('onParticipantDisConnected triggered:', participant);
    },

    onRecordingStatusChanged: status => {
        logger.debug('onRecordingStatusChanged triggered:', status);
    },

    onRoomMessage: data => {
        logger.debug('onRoomMessage triggered:', data);
        if (data?.type === CALL_LINK_UPDTAE) {
            Store.dispatch(setCallLink(data?.message?.callLink));
        } else if (data?.type === CALL_TYPE_UPDATE) {
            const newCallType = data?.message?.callType;
            Store.dispatch(setCallType(newCallType));
            if (newCallType?.toUpperCase() === CALL_TYPE.VIDEO) {
                toggleCallKitVideo(true);
            }
        } else if (data?.type === CALL_MODE_UPDATE) {
            Store.dispatch(setCallMode(data?.message?.callMode));
        } else if (data?.message === 'timeout') {
            const { callScreenStatus, ongoingCallParticipants } = Store.getState().callkitData;
            const currentUserId = getLocalUserName();
            const localUser = (ongoingCallParticipants || []).find(p => p.userId === currentUserId);
            const isConnectingState = localUser?.callStatus === CALLKIT_CALLSTATUS.TRY_TO_CONNECT;

            if (callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE && isConnectingState) {
                showWarningToast(`${i18n.t('common.INTERNET_ERROR_MESSAGE')}`);
                clearLegacyCallState();
                Store.dispatch(resetCallkitData());
            }
        }
    },

    onRoomConnectionFailure: error => {
        logger.debug('onRoomConnectionFailure triggered:', error);
    },

    onCallSwitch: callData => {
        logger.debug('onCallSwitch triggered:', callData);
        if (callData?.switchAction === CALL_SWITCH_CANCEL || callData?.switchAction === CALL_SWITCH_ACCEPT) {
            Store.dispatch(updateCallSwitch({ switchAction: null }));
            if (callData?.switchAction === CALL_SWITCH_ACCEPT) {
                Store.dispatch(setCallType(CALL_TYPE.VIDEO));
                toggleCallKitVideo(true);
            }
        } else {
            Store.dispatch(updateCallSwitch(callData));
        }
    },

    onActiveSpeakerChange: speaker => {
        const { audioLevel, identity: userId } = speaker;
        if (audioLevel > 0.125) {
            Store.dispatch(swapSpeakerIntoVisible({ userId, localUser: getLocalUserName() }));
        }
    },

    onAudioTrackMuted: (participantInfo, isMuted) => {
        logger.debug('onAudioTrackMuted triggered:', participantInfo, isMuted);
        Store.dispatch(updateParticipantStatus({ userId: participantInfo.identity, isAudioMuted: isMuted }));
    },

    onVideoTrackMuted: (participantInfo, isMuted) => {
        logger.debug('onVideoTrackMuted triggered:', participantInfo, isMuted);
        Store.dispatch(updateParticipantStatus({ userId: participantInfo.identity, isVideoMuted: isMuted }));
    },

    onConnectionQualityChanged: () => {},

    onTrackUpdated: trackInfo => {
        logger.debug('onTrackUpdated triggered:', trackInfo);
        const userId = trackInfo.identity;
        const isLocal = trackInfo.isLocalUser;
        const updates = { userId };

        const audioTrack = (trackInfo.tracks || []).find(
            t => t.kind === 'audio' && (t.source || t.track?.source) !== 'screen_share_audio',
        );
        const videoTrack = (trackInfo.tracks || []).find(
            t => t.kind === 'video' && (t.source || t.track?.source) !== 'screen_share',
        );

        // For remote participants, if a track is missing from the delta info, do not forcefully mute it
        if (!isLocal) {
            if (audioTrack !== undefined) updates.isAudioMuted = audioTrack.isMuted;
            if (videoTrack !== undefined) updates.isVideoMuted = videoTrack.isMuted;
        } else {
            // For the local user, we only update state if the track is explicitly provided in the info
            if (audioTrack) updates.isAudioMuted = audioTrack.isMuted;
            if (videoTrack) updates.isVideoMuted = videoTrack.isMuted;
        }

        trackInfo.tracks?.forEach(t => {
            const isScreenShare = (t.source || t.track?.source) === 'screen_share';
            const isRemoved =
                t.type === 'remove' ||
                trackInfo.type === 'remove' ||
                t.action === 'remove' ||
                trackInfo.action === 'remove';

            if (t.track && !isRemoved) {
                if (isScreenShare) {
                    const screenUserId = userId + '-screen';
                    CallMediaStore.addTrack(screenUserId, t.track, 'video');

                    const exists = Store.getState().callkitData.ongoingCallParticipants.some(
                        p => p.userId === screenUserId,
                    );
                    logger.debug('exists', exists);
                    if (!exists) {
                        logger.debug('screenshare connected');
                        Store.dispatch(
                            addParticipantToCall({
                                userId: screenUserId,
                                callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                                isAudioMuted: true,
                                isVideoMuted: false,
                            }),
                        );
                    } else {
                        Store.dispatch(
                            updateParticipantStatus({
                                userId: screenUserId,
                                trackUpdated: Date.now(),
                                isVideoMuted: t.track.isMuted !== undefined ? t.track.isMuted : false,
                                callStatus: CALLKIT_CALLSTATUS.CONNECTED, // Ensure status is resumed on re-publication
                            }),
                        );
                    }
                } else {
                    const trackType = t.kind;
                    CallMediaStore.addTrack(userId, t.track, trackType);
                    updates.trackUpdated = Date.now(); // Force UI update
                    // Not for a peer on hold: the hold's own unpublish events would undo it.
                    const heldStatus = Store.getState().callkitData.ongoingCallParticipants.find(
                        p => p.userId === userId,
                    )?.callStatus;
                    if (heldStatus !== CALLKIT_CALLSTATUS.ON_HOLD) {
                        updates.callStatus = CALLKIT_CALLSTATUS.CONNECTED;
                    }
                }
            } else if (isScreenShare) {
                const screenUserId = userId + '-screen';
                Store.dispatch(removeParticipantFromCall(screenUserId));
                CallMediaStore.removeTrack(screenUserId, 'video');
            }
        });

        // Cleanup fallback: If the screenshare is completely dropped/unpublished, it will not appear in the tracks array.
        if (!isLocal) {
            const hasScreenShareTrack = (trackInfo.tracks || []).some(
                t => (t.source || t.track?.source) === 'screen_share',
            );

            if (!hasScreenShareTrack) {
                const screenUserId = userId + '-screen';
                const screenExists = Store.getState().callkitData.ongoingCallParticipants.some(
                    p => p.userId === screenUserId,
                );

                if (screenExists) {
                    logger.debug('Screenshare track missing from updates. Removing from UI.', screenUserId);
                    Store.dispatch(removeParticipantFromCall(screenUserId));
                    CallMediaStore.removeTrack(screenUserId, 'video');
                }
            }
        }

        if (Object.keys(updates).length > 1) {
            Store.dispatch(updateParticipantStatus(updates));
        }
    },

    onCallStatusChanged: async data => {
        logger.debug('onCallStatusChanged triggered:', data);
        const userId = data?.identity;
        const currentUserId = getLocalUserName();
        const ongoingCallParticipantsBefore = Store.getState().callkitData.ongoingCallParticipants;
        const oldStatus = ongoingCallParticipantsBefore.find(p => p.userId === userId)?.callStatus;

        // CALL-RESUME reports a transition rather than a state to hold: the participant is
        // connected again. Storing it verbatim would leave a status nothing else recognises.
        const storedStatus =
            data?.callStatus === CALLKIT_CALLSTATUS.CALL_RESUME ? CALLKIT_CALLSTATUS.CONNECTED : data?.callStatus;

        Store.dispatch(updateParticipantStatus({ userId: userId, callStatus: storedStatus }));

        // Synchronize status with screenshare tile if it exists
        const screenUserId = userId + '-screen';
        const screenParticipant = ongoingCallParticipantsBefore?.find(p => p.userId === screenUserId);
        if (screenParticipant) {
            Store.dispatch(updateParticipantStatus({ userId: screenUserId, callStatus: storedStatus }));
        }

        if (data?.callStatus === CALLKIT_CALLSTATUS.CALL_RESUME) {
            // A transition, not a state: connected again, and the hold's mute flags come off.
            Store.dispatch(
                updateParticipantStatus({
                    userId: userId,
                    callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                    isAudioMuted: false,
                    isVideoMuted: Store.getState().callkitData.callType?.toUpperCase() !== CALL_TYPE.VIDEO,
                }),
            );
            if (!isMatchingUserId(userId, currentUserId)) {
                closeIncomingCallNotification();
                Store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
            }
        } else if (!isMatchingUserId(userId, currentUserId) && data?.callStatus === CALLKIT_CALLSTATUS.CONNECTED) {
            closeIncomingCallNotification();
            Store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
        } else if (data?.callStatus === CALLKIT_CALLSTATUS.ON_HOLD) {
            // Already in the list: only the status changes, or the mute flags would reset.
            Store.dispatch(updateParticipantStatus({ userId: userId, callStatus: data?.callStatus }));
        } else if (
            data?.callStatus === CALLKIT_CALLSTATUS.CALLING ||
            data?.callStatus === CALLKIT_CALLSTATUS.RINGING ||
            data?.callStatus === CALLKIT_CALLSTATUS.CONNECTING ||
            data?.callStatus === CALLKIT_CALLSTATUS.TRY_TO_CONNECT ||
            data?.callStatus === CALLKIT_CALLSTATUS.RECONNECTING
        ) {
            if (data?.callStatus === CALLKIT_CALLSTATUS.RINGING && !isMatchingUserId(userId, currentUserId)) {
                const users = Store.getState().userInfoData?.users || {};
                const callkitData = Store.getState().callkitData;
                if (callkitData.callScreenStatus === CALLKIT_CALLSCREENSTATUS.IDLE) {
                    showIncomingCallNotification(
                        {
                            from: userId,
                            callType: callkitData.callType,
                            callMode: callkitData.callMode,
                            callId: callkitData.incomingCallDetails?.callId,
                            callerNickName: users[userId]?.nickName || userId,
                        },
                        users,
                    );
                }
            }
            const isVideoCall = Store.getState().callkitData.callType?.toUpperCase() === CALL_TYPE.VIDEO;
            const isLocal = isMatchingUserId(userId, currentUserId);

            // Capture mute state BEFORE any dispatches to preserve user-set mute state through reconnection
            const currentParticipant = Store.getState().callkitData.ongoingCallParticipants.find(
                p => p.userId === userId,
            );
            const preservedAudioMuted = isLocal ? (currentParticipant?.isAudioMuted ?? false) : true;
            const preservedVideoMuted = isLocal ? (currentParticipant?.isVideoMuted ?? !isVideoCall) : true;

            Store.dispatch(
                addParticipantToCall({
                    userId: userId,
                    callStatus: data?.callStatus,
                    isAudioMuted: preservedAudioMuted,
                    isVideoMuted: preservedVideoMuted,
                }),
            );
            Store.dispatch(
                updateParticipantStatus({
                    userId: userId,
                    callStatus: data?.callStatus,
                    isAudioMuted: preservedAudioMuted,
                    isVideoMuted: preservedVideoMuted,
                }),
            );
        } else if (
            data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED ||
            data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED ||
            data?.callStatus === CALLKIT_CALLSTATUS.REJECTED ||
            data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT
        ) {
            if (isEnabled('missedCallLog')) {
                const { callScreenStatus } = Store.getState().callkitData;
                if (
                    callScreenStatus === CALLKIT_CALLSCREENSTATUS.INCOMING &&
                    (data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED ||
                        data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT)
                ) {
                    getCallLogs({ size: 1 }, success => {
                        if (success && success.missedCount !== undefined) {
                            Store.dispatch(setMissedCallCount(success.missedCount));
                        }
                    });
                }
            }

            if (isIncomingNotificationActive()) {
                const users = Store.getState().userInfoData?.users || {};
                const callkitData = Store.getState().callkitData;
                // Only show missed call if it was DISCONNECTED (hung up) or TIMEOUT (ignored)
                if (
                    data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED ||
                    data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT
                ) {
                    showMissedCallNotification(
                        {
                            from: userId,
                            callType: callkitData.callType,
                            callMode: callkitData.callMode,
                            callerNickName: users[userId]?.nickName || userId,
                        },
                        users,
                    );
                }
            }
            closeIncomingCallNotification();
            const { ongoingCallParticipants, callMode } = Store.getState().callkitData;
            const remoteParticipants = ongoingCallParticipants.filter(p => !isMatchingUserId(p.userId, currentUserId));
            const isGroupCall = callMode === CALL_MODES.ONETOMANY || callMode === CALL_MODES.MEET;

            if (
                oldStatus !== data.callStatus &&
                (isGroupCall || remoteParticipants.length > 1) &&
                !isMatchingUserId(userId, currentUserId)
            ) {
                const { callScreenStatus, ongoingCallParticipants: currentParticipants } = Store.getState().callkitData;

                // Precise Solution: Only show failure toasts if there is at least ONE OTHER remote participant still CONNECTED.
                const otherConnectedRemote = currentParticipants.find(
                    p =>
                        p.userId !== currentUserId &&
                        p.userId !== userId &&
                        p.callStatus === CALLKIT_CALLSTATUS.CONNECTED,
                );

                if (callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE && otherConnectedRemote) {
                    const users = Store.getState().userInfoData?.users || {};
                    const name = users[userId]?.nickName || userId;

                    if (data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED) {
                        showErrorToast(`${name} ${i18n.t('common.IS_ENGAGED')}`);
                    } else if (data?.callStatus === CALLKIT_CALLSTATUS.REJECTED) {
                        showErrorToast(`${name} ${i18n.t('common.IS_BUSY')}`);
                    } else if (data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT) {
                        showErrorToast(`${name} ${i18n.t('common.IS_NOT_ANSWERING')}`);
                    }
                }
            }

            // The block above needs another remote still connected, which a 1-1 call never has.
            // Without this the caller's screen just closes, giving no reason for the failure.
            if (
                !isGroupCall &&
                oldStatus !== data.callStatus &&
                data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED &&
                !isMatchingUserId(userId, currentUserId)
            ) {
                showErrorToast(`${resolveCallUserName(userId)} ${i18n.t('common.IS_ON_ANOTHER_CALL')}`);
            }

            const timeoutSec =
                data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED || data?.callStatus === CALLKIT_CALLSTATUS.REJECTED
                    ? 1200
                    : 0;
            // Delay the call log refresh slightly so the backend has time to commit the new call log
            setTimeout(() => {
                Store.dispatch(triggerCallLogRefresh());
            }, 1500);

            setTimeout(() => {
                if (isMatchingUserId(userId, currentUserId)) {
                    clearLegacyCallState();
                    Store.dispatch(resetCallkitData());
                } else {
                    Store.dispatch(removeParticipantFromCall(userId));
                    const { callMode, ongoingCallParticipants } = Store.getState().callkitData;
                    const remainingRemoteParticipants = ongoingCallParticipants.filter(
                        p => !isMatchingUserId(p.userId, currentUserId),
                    );
                    if (callMode !== CALL_MODES.MEET && remainingRemoteParticipants.length === 0) {
                        clearLegacyCallState();
                        Store.dispatch(resetCallkitData());
                    }
                }
            }, timeoutSec);
        }
    },

    onAudioLevelChange: () => {},

    onLocalTracks: localTracks => {
        logger.debug('onLocalTracks triggered:', localTracks);
        const screenshareTrackInfo = localTracks?.find(t => (t.source || t.track?.source) === 'screen_share');
        const currentUserId = getLocalUserName();
        if (screenshareTrackInfo) {
            const screenUserId = currentUserId + '-screen';
            CallMediaStore.addTrack(screenUserId, screenshareTrackInfo, 'video');
            const exists = Store.getState().callkitData.ongoingCallParticipants.some(p => p.userId === screenUserId);
            if (!exists) {
                Store.dispatch(
                    addParticipantToCall({
                        userId: screenUserId,
                        callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                        isAudioMuted: true, // Audio isn't captured via screen
                        isVideoMuted: false,
                    }),
                );
            } else {
                Store.dispatch(updateParticipantStatus({ userId: screenUserId, trackUpdated: Date.now() }));
            }
        }
    },
    onScreenShareState: state => {
        logger.debug('onScreenShareState triggered', state);
        if (state === CALL_KIT_SCREENSHARE_STATE.STOP) {
            const currentUserId = getLocalUserName();
            const screenUserId = currentUserId + '-screen';
            Store.dispatch(removeParticipantFromCall(screenUserId));
            CallMediaStore.removeTrack(screenUserId, 'video');
            Store.dispatch(updateParticipantStatus({ userId: currentUserId, trackUpdated: Date.now() }));
        }
    },

    onCallKithelper: {
        getDisplayName: () => {
            return 'Anonymous user ';
        },
        getImageUrl: () => {
            return '';
        },
    },
};
